import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout';
import { Dashboard } from './pages/Dashboard';
import { Orders } from './pages/Orders';
import { Customers } from './pages/Customers';
import { Slots } from './pages/Slots';
import { Reports } from './pages/Reports';
import { Pricing } from './pages/Pricing';
import { Locations } from './pages/Locations';
import { Admin } from './pages/Admin';
import { WhatsApp } from './pages/WhatsApp';
import { Login } from './pages/Auth';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { NotificationProvider } from './contexts/NotificationContext';
import { hasPermission } from './utils/permissions';
import './styles/global.css';

const ROUTE_PERMS = [
  { path: '/', perm: 'dashboard' },
  { path: '/orders', perm: 'orders' },
  { path: '/customers', perm: 'customers' },
  { path: '/slots', perm: 'slots' },
  { path: '/reports', perm: 'reports' },
  { path: '/pricing', perm: 'pricing' },
  { path: '/locations', perm: 'locations' },
  { path: '/admin', perm: 'admin' },
];

function firstAllowedPath() {
  const match = ROUTE_PERMS.find((r) => hasPermission(r.perm));
  return match ? match.path : null;
}

function ProtectedRoute({ children }) {
  const isAuthenticated = localStorage.getItem('authToken');
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasPermission('dashboard')) {
    const fallback = firstAllowedPath();
    if (fallback && fallback !== '/') return <Navigate to={fallback} replace />;
    return <div style={{ padding: 32 }}>You don't have access to any section. Contact your admin.</div>;
  }
  return children;
}

function AuthRoute({ children }) {
  const isAuthenticated = localStorage.getItem('authToken');
  return isAuthenticated ? <Navigate to="/" replace /> : children;
}

function PermissionRoute({ perm, children }) {
  if (!localStorage.getItem('authToken')) {
    return <Navigate to="/login" replace />;
  }
  if (!hasPermission(perm)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function ProtectedLayout({ children }) {
  return (
    <NotificationProvider>
      <Layout>{children}</Layout>
    </NotificationProvider>
  );
}

function Guarded({ perm, children }) {
  return (
    <PermissionRoute perm={perm}>
      <ProtectedLayout>{children}</ProtectedLayout>
    </PermissionRoute>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        <Route
          path="/login"
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <Dashboard />
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/orders" element={<Guarded perm="orders"><Orders /></Guarded>} />
        <Route path="/customers" element={<Guarded perm="customers"><Customers /></Guarded>} />
        <Route path="/slots" element={<Guarded perm="slots"><Slots /></Guarded>} />
        <Route path="/reports" element={<Guarded perm="reports"><Reports /></Guarded>} />
        <Route path="/pricing" element={<Guarded perm="pricing"><Pricing /></Guarded>} />
        <Route path="/locations" element={<Guarded perm="locations"><Locations /></Guarded>} />
        <Route path="/admin" element={<Guarded perm="admin"><Admin /></Guarded>} />
        <Route path="/whatsapp" element={<Guarded perm="admin"><WhatsApp /></Guarded>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
